import React from "react";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { observable } from "mobx";
import classNames from "classnames";
import { Icon, Modal, Button, Select, Form, Card } from "semantic-ui-react";
import DnDItem from "~/components/element/DnDItem";
import Immersive3dTourPanorama from "./Immersive3dTourPanorama";
import Immersive3dTourFloorModel from "../../models/Immersive3dTourFloor";
import EditableLabel from "../EditableLabel";

@observer
class Immersive3dTourFloor extends React.Component {
  constructor(props) {
    super(props);

    const { remainingFloors } = this.props;
    const movePanosToFloorId = remainingFloors.length > 0 ? remainingFloors[0].id : "";

    this.state = {
      showConfirmDelete: false,
      movePanosToFloorId,
    };
  }

  static getDerivedStateFromProps(props, state) {
    /** @type {Array} */
    const { remainingFloors } = props;
    const { movePanosToFloorId } = state;

    // If there no remaining floors, then whatever pre-existing value should be nulled.
    if (remainingFloors.length === 0) {
      return { movePanosToFloorId: null };
    }

    // if there's no movePanosToFloorId set, then let's set it to the first remaining floor.
    if (!movePanosToFloorId) {
      const [floor] = remainingFloors;
      return { movePanosToFloorId: floor.id };
    }

    // If there used to be a floor id, but the floor no longer exists, then reset the id to the first available floor.
    if (!remainingFloors.find(f => f.id === movePanosToFloorId)) {
      const [floor] = remainingFloors;
      return { movePanosToFloorId: floor.id };
    }

    return null;
  }

  onDeleteClick() {
    const { floor, remainingFloors, onDelete } = this.props;

    // Deleting the last floor shouldn't be allowed.
    if (remainingFloors.length === 0) {
      throw new Error("Cannot delete last floor");
    }

    if (floor.panoramas.length === 0) {
      // IF the floor has no panos, then do not bother to show a confirm popup.
      onDelete(floor.id);
    } else {
      this.setState({ showConfirmDelete: true });
    }
  }

  onDeleteConfirm() {
    const { floor, onDelete } = this.props;
    const { movePanosToFloorId } = this.state;

    //  Close first, then delete, otherwise if we delete first, then the component will unmount and setState
    // won't work.
    this.setState({ showConfirmDelete: false }, () => onDelete(floor.id, movePanosToFloorId));
  }

  render() {
    const {
      forwardedRef,
      isBucketFloor,
      className,
      floor,
      remainingFloors,
      floorsDisabled,
      labelEditDisabled,
      draggingPanoramaId,
      deletePanoramaCallback,
      openProcessingImageModalCallback,
      onPanoramaDragBeginCallback,
      onPanoramaDragEndCallback,
      floorNameChangeCallback,
      onPanoramaClick,
      thumbnails,
      selectedPanoramasCount,
    } = this.props;

    const { showConfirmDelete, movePanosToFloorId } = this.state;

    const showDeleteIcon = !floorsDisabled && remainingFloors.length > 0;

    return (
      <div
        ref={forwardedRef}
        className={classNames(className, "immersive3dTourFloor", {
          error: floor.panoramas.length > 0 && floor.hasErrors,
          warning: floor.panoramas.length > 0 && floor.hasWarnings && !floor.hasErrors,
          info: floor.panoramas.length > 0 && floor.hasInfos && !floor.hasWarnings && !floor.hasErrors,
          // hide the bucket floor when all panos has been dragged to real floors.
          "bucket message orange ui": isBucketFloor,
          hidden: isBucketFloor && floor.panoramas.length === 0,
        })}
      >
        {showDeleteIcon && <Icon name="window close" onClick={() => this.onDeleteClick()} />}
        <div className="immersive3dTourPanoramaHeader">
          <EditableLabel
            value={floor.name}
            error={floor.error}
            editDisabled={floorsDisabled || labelEditDisabled}
            onChange={value => floorNameChangeCallback(floor.id, value)}
          />
        </div>
        <Card.Group itemsPerRow={5} className="immersive3dTourPanoramas">
          {floor.panoramas.map(panorama => (
            <DnDItem
              className="immersive3dTourPanorama ui card"
              key={panorama.id}
              disabled={floorsDisabled}
              selected={panorama.isSelected}
              draggingItemId={draggingPanoramaId}
              onClick={(item, event) => onPanoramaClick(panorama.id, event)}
              item={observable({
                id: panorama.id,
                type: "panorama",
                // prevent becoming a droppable area. Only floors can accept drops.
                accept: [],
                floorId: floor.id,
              })}
              onDragEnd={onPanoramaDragEndCallback}
              onDragBegin={onPanoramaDragBeginCallback}
            >
              <Immersive3dTourPanorama
                panorama={panorama}
                panoramaIndex={floor.panoramaIndex(panorama) + 1}
                deletePanoramaCallback={deletePanoramaCallback}
                openProcessingImageModalCallback={openProcessingImageModalCallback}
                thumbnails={thumbnails}
                selectedPanoramasCount={selectedPanoramasCount}
              />
            </DnDItem>
          ))}
        </Card.Group>
        <Modal open={showConfirmDelete} onClose={() => this.setState({ showConfirmDelete: false })}>
          <Modal.Header>Delete Floor</Modal.Header>
          <Modal.Content>
            <p>Are you sure you want to delete Floor {floor.name}?</p>
            <Form>
              <label>Transfer panoramas to floor:</label>
              <br />
              <br />
              <Select
                value={movePanosToFloorId}
                options={remainingFloors.map(f => ({
                  key: f.id,
                  value: f.id,
                  text: f.name,
                }))}
                onChange={(_event, { value }) => this.setState({ movePanosToFloorId: value })}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button primary onClick={() => this.onDeleteConfirm()}>
              Delete
            </Button>
            <Button secondary onClick={() => this.setState({ showConfirmDelete: false })}>
              Cancel
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

Immersive3dTourFloor.propTypes = {
  floor: PropTypes.instanceOf(Immersive3dTourFloorModel),
  floorsDisabled: PropTypes.bool,
  labelEditDisabled: PropTypes.bool,
  // the remaining floors
  remainingFloors: PropTypes.arrayOf(PropTypes.instanceOf(Immersive3dTourFloorModel)),
  deletePanoramaCallback: PropTypes.func.isRequired,
  openProcessingImageModalCallback: PropTypes.func.isRequired,
  onPanoramaDragBeginCallback: PropTypes.func.isRequired,
  onPanoramaDragEndCallback: PropTypes.func.isRequired,
  floorNameChangeCallback: PropTypes.func,
  onPanoramaClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  isBucketFloor: PropTypes.bool,
  forwardedRef: PropTypes.any,
  thumbnails: PropTypes.object.isRequired,
  draggingPanoramaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedPanoramasCount: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
};

Immersive3dTourFloor.defaultProps = {
  className: "",
  floorsDisabled: false,
  labelEditDisabled: false,
  remainingFloors: [],
  floorNameChangeCallback: () => {},
  onDelete: () => {},
};

const Immersive3dTourFloorWithRef = React.forwardRef((props, ref) => (
  <Immersive3dTourFloor {...props} forwardedRef={ref} />
));

export default Immersive3dTourFloorWithRef;
